import React, {
  useState, useCallback, useMemo, useEffect, useRef,
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Empty,
  Row,
  notification,
} from 'antd';
import { DateTime } from 'luxon';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import { Gantt, ViewMode } from 'web-dashboard-gantt';
import { Common, Schedule } from 'ontraccr-common';
import { InfoCircleTwoTone } from '@ant-design/icons';
import Permissions from '../../auth/Permissions';
import BreadCrumbContainer from '../../common/breadcrumbContainer/breadcrumbContainer';
import {
  workingDayMap,
  findDateRangeOverlap,
  filterOnProperty,
  calculateDateDifferenceInDays,
  getVisibleColumnMap,
  importFile,
  calculateDuration,
  parseGanttCostCodeKey,
  DEFAULT_WEEK_HEADER_DISPLAY,
  buildGanttVisibleColumns,
} from './ganttScheduleHelpers';

import 'web-dashboard-gantt/dist/index.css';

import {
  getGanttSchedules,
  getGanttScheduleRows,
  getGanttScheduleHolidays,
  getGanttScheduleSettings,
  updateGanttScheduleSettings,
  importGanttSchedule,
  addGanttScheduleRows,
  updateGanttScheduleRow,
  deleteGanttScheduleRow,
  getGanttScheduleLabels,
  updateGanttScheduleFilters,
  getUserGanttScheduleFilterViews,
  resetGanttState,
  createGanttSchedule,
} from '../state/schedule.actions';
import {
  getProjects,
} from '../../projects/state/projects.actions';
import {
  getUserLabels,
  getUsers,
  getUserSettings,
  updateUserSettings,
} from '../../users/state/users.actions';
import {
  getAllPhases,
} from '../../costcodes/state/costcodes.actions';
import useUpdateCostcodeIdMap from '../../costcodes/useUpdateCostcodeIdMap';
import {
  getEquipment,
} from '../../equipment/state/equipment.actions';
import {
  getMaterials,
} from '../../materials/state/materials.actions';
import { toggleFullscreen } from '../../main/state/main.actions';
import { getDivisions } from '../../settings/state/settings.actions';

import GanttScheduleImportDrawer from './GanttScheduleImportDrawer';
import GanttScheduleSettingsDrawer from './Settings/GanttScheduleSettingsDrawer';
import GanttScheduleAddDrawer from './GanttScheduleAddDrawer';
import sortByString, { getIdMap, includesTerm, sortByLocalCompare } from '../../helpers/helpers';
import GanttScheduleHeader from './GanttScheduleHeader';
import GanttScheduleList from './GanttScheduleList';
import GanttScheduleExportDrawer from './GanttScheduleExportDrawer';
import { GANTT_EXPORT_VIEW_TYPES } from './ganttScheduleExport.helpers';
import GanttScheduleCustomFilterTab from './Filters/GanttScheduleCustomFilterTab';
import FilterDrawer from '../../common/filters/FilterDrawer';
import GanttScheduleDispatchDrawer from './GanttScheduleDispatchDrawer';
import HoverHelp from '../../common/HoverHelp';
import FieldShiftModalSelector from './FieldShiftModalSelector';
import CustomConfirmModal from '../../common/modals/CustomConfirmModal';
import { COSTCODE_BY_IDS_COLUMNS } from '../../reports/reportHelpers';

const crumbs = [{ text: 'Scheduling', icon: 'calendar' }];

export default function GanttSchedule({
  history,
  projectId: selectedProjectId,
}) {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const {
    projects,
  } = useSelector((state) => state.projects);
  const {
    ganttSchedules,
    ganttScheduleRows,
    ganttScheduleSettings = {
      workingDays: [],
    },
    ganttScheduleHolidays,
    ganttScheduleLabels,
    ganttScheduleFilters,
    ganttScheduleFilterViews,
  } = useSelector((state) => state.schedule);
  const {
    settings: userSettings = {
      ganttScheduleSettings: JSON.stringify({
        visibleColumns: ['Name'],
      }),
    },
  } = useSelector((state) => state.users);
  const {
    selectedDivisions,
  } = useSelector((state) => state.settings);
  const isFullScreen = useSelector((state) => state.main.fullscreen);
  const scale = useSelector((state) => state.schedule.scale);
  const {
    costcodeIdMap: costCodeMap,
    phases,
  } = useSelector((state) => state.costcodes);
  const equipment = useSelector((state) => state.equipment.equipment);
  const users = useSelector((state) => state.users.users);
  const materials = useSelector((state) => state.materials.materials);
  const positions = useSelector((state) => state.settings.positionNames);
  const userLabels = useSelector((state) => state.labels);

  const [viewType, setViewType] = useState(ViewMode.Month);
  const [visibleDrawer, setVisibleDrawer] = useState('');
  const [scheduleId, setScheduleId] = useState('');
  const [projectId, setProjectId] = useState(selectedProjectId);
  const [viewDate, setViewDate] = useState(DateTime.local());
  const [newTasks, setNewTasks] = useState([]);
  const [newHolidays, setNewHolidays] = useState([]);
  const [newSettings, setNewSettings] = useState({});
  const [columnWidth, setColumnWidth] = useState(150);
  const [selectedTask, setSelectedTask] = useState({});
  const [selectedSchedule, setSelectedSchedule] = useState({});
  const [documentHeight, setDocumentHeight] = useState(document.body.offsetHeight);
  const [activeTab, setActiveTab] = useState('schedule');
  const [sortType, setSortType] = useState('default');
  const [dispatchDrawerProps, setDispatchDrawerProps] = useState({
    visible: false,
    row: {},
  });
  const [showFieldShiftSelector, setShowFieldShiftSelector] = useState(false);

  const inputRef = useRef();

  const isMaster = !projectId;

  useEffect(() => {
    dispatch(getGanttSchedules());
    dispatch(getProjects());
    dispatch(getUserSettings());
    dispatch(getGanttScheduleLabels());
    dispatch(getUsers());
    dispatch(getEquipment());
    dispatch(getMaterials());
    dispatch(getDivisions());
    dispatch(getUserLabels());
  }, []);

  useEffect(() => {
    setViewDate(viewDate.plus({ seconds: 1 }));
  }, [scale]);

  useEffect(() => {
    if (scheduleId) {
      dispatch(getGanttScheduleRows(scheduleId));
      dispatch(getGanttScheduleSettings(scheduleId));
      dispatch(getGanttScheduleHolidays(scheduleId));
      dispatch(getAllPhases());
      dispatch(getUserGanttScheduleFilterViews(scheduleId));
      dispatch(updateGanttScheduleFilters());
    } else {
      resetGanttState();
    }
  }, [scheduleId]);

  const [exportVisibleColumnNames, setExportVisibleColumnNames] = useState([]);
  const [exportViewType, setExportViewType] = useState(GANTT_EXPORT_VIEW_TYPES.GANTT);

  const hideDrawer = useCallback(() => {
    setVisibleDrawer('');
    setSelectedTask({});
    setExportVisibleColumnNames([]);
    setExportViewType(GANTT_EXPORT_VIEW_TYPES.GANTT);
  }, []);

  const openDrawer = useCallback((type) => {
    setVisibleDrawer(type);
  }, []);

  const hideDispatchDrawer = useCallback(() => {
    const { row = {} } = dispatchDrawerProps;

    setDispatchDrawerProps({ visible: false });

    if (!Common.isObjectEmpty(row)) {
      hideDrawer();
    }
  }, [dispatchDrawerProps, hideDrawer]);

  const showDispatchDrawer = useCallback(({ row } = {}) => {
    if (row) {
      setSelectedTask(row);
      setDispatchDrawerProps({ visible: true, row });
    } else {
      setDispatchDrawerProps({ visible: true });
    }
  }, []);

  const showShiftSelectorModal = useCallback(({ row }) => {
    setSelectedTask(row);
    setShowFieldShiftSelector(true);
  }, []);

  const onShiftClick = useCallback((shiftId) => {
    setShowFieldShiftSelector(false);
    setDispatchDrawerProps({ visible: true, shiftId, isDisplay: true });
  }, []);

  const onDateChange = useCallback((newDate) => {
    const ts = newDate.valueOf();
    const dt = DateTime.fromMillis(ts);
    setViewDate(dt);
  }, []);

  const onViewTypeChange = useCallback((newViewType) => {
    setViewType(newViewType);

    if (newViewType === 'month') {
      setColumnWidth(150);
      return;
    }

    setColumnWidth(275);
    setViewDate(viewDate.plus({ seconds: 1 }));
  }, [viewType]);

  const onEventClick = useCallback((task) => {
    setSelectedTask(ganttScheduleRows.find((row) => row.id === task.id));
    openDrawer('add');
  }, [ganttScheduleRows]);

  const filteredProjects = useMemo(() => projects.filter(
    (project) => selectedDivisions.has(project.divisionId),
  ), [projects, selectedDivisions]);

  const userMap = useMemo(() => getIdMap(users), [users]);
  const labelMap = useMemo(() => getIdMap(ganttScheduleLabels), [ganttScheduleLabels]);
  const equipmentMap = useMemo(() => getIdMap(equipment), [equipment]);

  const flattenedCostcodeItems = useMemo(() => {
    const seen = new Set();
    const items = [];
    ganttScheduleRows.forEach((row) => {
      row.costCodes?.forEach((costCode) => {
        const [, costCodeId] = costCode.split('.');
        if (costCodeId && !seen.has(costCodeId)) {
          seen.add(costCodeId);
          items.push({ costcodeId: costCodeId });
        }
      });
    });
    return items;
  }, [ganttScheduleRows]);

  useUpdateCostcodeIdMap({
    items: flattenedCostcodeItems,
    columns: COSTCODE_BY_IDS_COLUMNS.GANTT,
  });

  const taskMap = useMemo(() => getIdMap(ganttScheduleRows), [ganttScheduleRows]);
  const projectMap = useMemo(() => getIdMap(projects), [projects]);
  const filteredProjectMap = useMemo(() => getIdMap(filteredProjects), [filteredProjects]);
  const scheduleMap = useMemo(() => getIdMap(ganttSchedules), [ganttSchedules]);
  const positionMap = useMemo(() => getIdMap(positions), [positions]);
  const userLabelMap = useMemo(() => getIdMap(userLabels), [userLabels]);

  useEffect(() => {
    if (scheduleId) {
      setSelectedSchedule(scheduleMap[scheduleId] || {});
    } else {
      setSelectedSchedule({});
    }
  }, [scheduleId, scheduleMap]);

  const phaseMap = useMemo(() => phases.reduce((acc, phase) => {
    if (!acc[phase.id]) {
      acc[phase.id] = [];
    }

    acc[phase.id].push(phase);
    return acc;
  }, {}), [phases]);

  const filteredTasks = useMemo(() => {
    const {
      name: nameFilter,
      dateRange: dateRangeFilter,
      users: userFilter,
      equipment: equipmentFilter,
      costCodes: costCodeFilter,
      phases: phaseFilter,
      materials: materialFilter,
      labels: labelFilter,
      schedules: scheduleFilter,
      projectTypes: projectTypeFilter,
    } = ganttScheduleFilters;

    const userFilterMap = new Set(userFilter);
    const equipmentFilterMap = new Set(equipmentFilter);
    const costCodeFilterMap = new Set(costCodeFilter);
    const phaseFilterMap = new Set(phaseFilter);
    const materialFilterMap = new Set(materialFilter);
    const labelFilterMap = new Set(labelFilter);
    const scheduleFilterMap = new Set(scheduleFilter);
    const projectTypeFilterMap = new Set(projectTypeFilter);

    const filteredRows = [];
    let formattedRow;

    ganttScheduleRows.forEach((row) => {
      const schedule = scheduleMap[row.ganttScheduleId];
      let scheduleName = schedule.name;

      const relevantProject = projectMap?.[schedule?.projectId];
      if (relevantProject) {
        scheduleName = relevantProject?.name ?? '';
      }

      const expectedNumberOfUsers = Schedule.getExpectedUsersString({
        expectedUsers: row.expectedUsers,
        userLabelMap,
        positionMap,
      });

      const expectedUserCount = Schedule.getExpectedUserTotal({ row });

      formattedRow = {
        ...row,
        realStartDate: row.startDate,
        realEndDate: row.endDate,
        label: labelMap[row.labelId] || {},
        actualExpectedUserCount: {
          label: `${row.users.length}/${expectedUserCount}`,
          color: expectedUserCount === row.users.length ? 'green' : 'red',
          users: row.users.map((userId) => userMap[userId]),
        },
        expectedNumberOfUsers,
        usersColumn: row.users.map((userId) => userMap[userId]),
        equipmentColumn: row.equipment.map((equipmentId) => equipmentMap[equipmentId]),
        costCodeColumn: row.costCodes.map((codeObject) => {
          const [, costCodeId] = codeObject.split('.');
          return costCodeMap[costCodeId];
        }),
        phaseColumn: phaseMap[row.phaseId],
        materialColumn: row.materials.map((material) => ({
          ...materials[material.id],
          ...material,
        })),
        actualExpectedStart: calculateDateDifferenceInDays(row.startDate, row.actualStartDate),
        actualExpectedEnd: calculateDateDifferenceInDays(row.endDate, row.actualEndDate),
        dependencies: row.parentDependencies,
        parentDependenciesColumn: row.parentDependencies.map((taskId) => taskMap[taskId]),
        childDependenciesColumn: row.childDependencies.map((taskId) => taskMap[taskId]),
        schedule: scheduleName,
        isDisabled: isFullScreen,
      };

      if (
        (nameFilter && !includesTerm(formattedRow.name, nameFilter))
        || !filterOnProperty(formattedRow.users, userFilterMap)
        || !filterOnProperty(formattedRow.equipment, equipmentFilterMap)
        || !filterOnProperty(formattedRow.costCodes, costCodeFilterMap)
        || !filterOnProperty(formattedRow.phaseId, phaseFilterMap)
        || !filterOnProperty(
          formattedRow.materials.map((material) => material.id),
          materialFilterMap,
        )
        || !filterOnProperty(formattedRow.labelId, labelFilterMap)
        || !filterOnProperty(formattedRow.ganttScheduleId, scheduleFilterMap)
        || (formattedRow.projectId && !filteredProjectMap[formattedRow.projectId])
        || !filterOnProperty(relevantProject?.projectTypeId || 'None', projectTypeFilterMap)
      ) {
        return;
      }

      let formattedDateFilter = null;

      if (dateRangeFilter) {
        formattedDateFilter = {
          start: dateRangeFilter[0].startOf('day').utcOffset(0).valueOf() / 1000,
          end: dateRangeFilter[1].startOf('day').utcOffset(0).valueOf() / 1000,
        };
      } else {
        filteredRows.push({
          ...formattedRow,
        });
        return;
      }

      const overlappingDateRange = findDateRangeOverlap(
        {
          start: formattedRow.startDate,
          end: formattedRow.endDate,
        },
        formattedDateFilter,
      );

      if (!overlappingDateRange) {
        return;
      }

      // Set the end date to the end of the day in order for it to be rendered correctly
      const endDate = new Date(
        overlappingDateRange.end * 1000 + (new Date().getTimezoneOffset() * 60 * 1000),
      );
      endDate.setHours(23);
      endDate.setMinutes(59);

      filteredRows.push({
        ...formattedRow,
        realStartDate: formattedRow.startDate,
        realEndDate: formattedRow.endDate,
        startDate: overlappingDateRange.start,
        endDate: overlappingDateRange.end,
        start: new Date(
          overlappingDateRange.start * 1000 + (new Date().getTimezoneOffset() * 60 * 1000),
        ),
        end: endDate,
        isDisabled: isFullScreen,
      });
    });

    const isDescending = sortType?.includes('desc');
    switch (sortType) {
      case 'color':
        filteredRows.sort(sortByString('color'));
        break;
      case 'name-asc':
      case 'name-desc':
        filteredRows.sort(sortByLocalCompare('name', !isDescending));
        break;
      case 'duration-asc':
      case 'duration-desc':
        filteredRows.sort((a, b) => {
          const aVal = a?.duration ?? 0;
          const bVal = b?.duration ?? 0;
          return (aVal - bVal) * (isDescending ? -1 : 1);
        });
        break;
      case 'default':
      default:
        break;
    }

    return filteredRows;
  }, [
    isFullScreen,
    ganttScheduleFilters,
    userMap,
    labelMap,
    equipmentMap,
    costCodeMap,
    projectMap,
    scheduleMap,
    taskMap,
    materials,
    phaseMap,
    ganttScheduleRows,
    ganttScheduleSettings,
    ganttScheduleHolidays,
    selectedDivisions,
    filteredProjectMap,
    sortType,
  ]);

  const formattedSchedules = useMemo(() => ganttSchedules.map((schedule) => ({
    ...schedule,
    project: projectMap[schedule.projectId],
  })), [ganttSchedules, projectMap]);

  const filteredSchedules = useMemo(() => {
    const newSchedules = [];
    const projMap = { ...filteredProjectMap };

    formattedSchedules.forEach((schedule) => {
      if (!schedule.projectId || projMap[schedule.projectId]) {
        newSchedules.push(schedule);
        delete projMap[schedule.projectId];
      }
    });

    // Construct new schedules for any projects that don't have one
    Object.keys(projMap).forEach((projId) => {
      newSchedules.push({
        id: '',
        isNew: true,
        name: 'Default',
        project: projMap[projId],
        projectId: projId,
      });
    });

    return newSchedules;
  }, [
    formattedSchedules,
    filteredProjectMap,
  ]);

  // If the filtered schedules don't include the current schedule, clear the schedule
  useEffect(() => {
    if (!projectId) {
      return;
    }

    const schedule = filteredSchedules.find(
      ({ projectId: scheduleProjectId }) => scheduleProjectId === projectId,
    );

    if (!schedule) {
      setProjectId(null);
      setScheduleId(null);
    } else if (schedule.isNew) {
      const createSchedule = async () => {
        const newSchedule = await dispatch(createGanttSchedule({
          name: 'Default',
          projectId,
        }));

        if (newSchedule) {
          setScheduleId(newSchedule.id);
        }
      };

      createSchedule();
    } else {
      setScheduleId(schedule.id);
    }
  }, [projectId, filteredSchedules]);

  const nonWorkingDays = useMemo(() => {
    const dayMap = { ...workingDayMap };
    const days = [];

    if (ganttScheduleSettings.workingDays) {
      ganttScheduleSettings.workingDays.forEach((day) => {
        delete dayMap[day];
      });

      // Any keys left in the map must be a non-working day
      Object.keys(dayMap).forEach((day) => {
        // In luxon 7 is sunday, but in the gantt schedule 0 is sunday
        if (day === '7') {
          days.push(0);
          return;
        }
        days.push(parseInt(day, 10));
      });
    }

    return days;
  }, [ganttScheduleSettings]);

  const visibleColumnMap = useMemo(() => (
    getVisibleColumnMap({
      t,
      tasks: ganttScheduleRows,
      activeTab,
    })
  ), [ganttScheduleRows, activeTab, t]);

  const shiftConnectionFormatter = useCallback((value, record) => {
    const { shiftConnections = [] } = record;

    if (shiftConnections.length) {
      return (
        <button
          aria-label="action"
          type="button"
          style={{
            border: 'none', background: 'transparent', padding: 0, margin: 0,
          }}
          onClick={() => showShiftSelectorModal({ row: record })}
        >
          <HoverHelp
            content={`This task has been dispatched to ${shiftConnections?.length} shift${shiftConnections?.length === 1 ? '' : 's'}.`}
            Icon={InfoCircleTwoTone}
            iconProps={{ twoToneColor: '#fdb81b', style: { marginTop: 8, fontSize: '15px' } }}
          />
        </button>
      );
    }

    return null;
  }, []);

  const ganttUserSettings = useMemo(() => {
    if (userSettings.ganttScheduleSettings) {
      const projectSettings = JSON.parse(userSettings.ganttScheduleSettings);
      const realColumns = isMaster
        ? projectSettings.visibleColumns
        : projectSettings.visibleColumns.filter((column) => column !== 'Project');
      const visibleColumns = realColumns.map(
        (column) => visibleColumnMap[column],
      );

      const shiftConnectionColumn = {
        property: 'shiftConnections',
        width: 25,
        formatter: shiftConnectionFormatter,
      };

      projectSettings.visibleColumns = [shiftConnectionColumn, ...visibleColumns];

      return projectSettings;
    }

    return {
      visibleColumns: [visibleColumnMap.Name],
      weekHeaderDisplay: DEFAULT_WEEK_HEADER_DISPLAY,
    };
  }, [userSettings, isMaster, visibleColumnMap, shiftConnectionFormatter]);

  const onTaskClick = useCallback((task) => {
    setViewDate(DateTime.fromSeconds(task.startDate));
  }, [setViewDate]);

  const onFileChange = async (event) => {
    const file = event.target.files[0];
    const result = await importFile(file, notification);

    if (result) {
      setNewTasks(result.tasks);
      setNewHolidays(result.holidays);
      setVisibleDrawer('import');
      setNewSettings({
        workingDays: result.workingDays,
      });
    }
  };

  const onEventDateChange = useCallback(async ({
    id,
    ganttScheduleId,
    end,
    name,
    color,
    progress,
    labelId,
    users,
    costCodes,
    equipment,
    materials,
    phaseId,
    actualStartDate,
    actualEndDate,
    parentDependencies,
    childDependencies,
    startDate,
    endDate,
  }) => {
    const oldEnd = DateTime.fromMillis(endDate * 1000).toLocaleString(DateTime.DATE_MED);
    const newEndSQLDate = DateTime.fromJSDate(end).toSQLDate();
    const newEndDT = DateTime.fromSQL(newEndSQLDate, { zone: 'utc' });
    const newEnd = newEndDT.toLocaleString(DateTime.DATE_MED);

    const res = await new Promise((resolve) => (
      CustomConfirmModal({
        title: 'Update Date',
        content: `Are you sure you want to update the date for ${name} from ${oldEnd} to ${newEnd}?`,
        onOk: async () => {
          const newEndSeconds = Math.floor(newEndDT.endOf('day').set({ seconds: 0, millisecond: 0 }).toSeconds());
          const updatedTask = {
            name,
            color,
            type: 'task',
            priority: 0,
            startDate,
            endDate: newEndSeconds,
            duration: calculateDuration(
              startDate,
              newEndSeconds,
              ganttScheduleSettings.workingDays,
              ganttScheduleHolidays,
            ),
            progress,
            labelId,
            users,
            costCodes: costCodes?.map(parseGanttCostCodeKey),
            equipment,
            materials,
            phaseId,
            actualStartDate,
            actualEndDate,
            parentDependencies,
            childDependencies,
          };

          await dispatch(
            updateGanttScheduleRow(
              ganttScheduleId,
              id,
              updatedTask,
            ),
          );

          resolve(true);
        },
        onCancel: () => {
          // Resolve with false to indicate that the user cancelled the action
          resolve(false);
        }
      })
    ));

    return res;
  }, [
    ganttScheduleSettings,
    ganttScheduleHolidays,
  ]);

  const onImport = () => {
    inputRef.current.click();
  };

  const onImportSubmit = async (payload) => {
    const result = await dispatch(importGanttSchedule(scheduleId, payload));

    if (result) {
      hideDrawer();
    }
  };

  const onSettingsSubmit = async (payload) => {
    const result = await dispatch(updateGanttScheduleSettings(scheduleId, payload));

    if (result) {
      hideDrawer();
    }
  };

  const onUserSettingsSubmit = async (payload) => {
    const result = await dispatch(updateUserSettings(payload));

    if (result) {
      hideDrawer();
    }
  };

  const onAddSubmit = async (payload) => {
    const expectedUsers = payload.expectedUsers.filter(({ count, type }) => count > 0 || type === 'default');

    const addPayload = {
      name: payload.name,
      color: payload.color,
      duration: payload.duration,
      startDate: payload.start.valueOf() / 1000,
      endDate: payload.end,
      type: 'Task',
      priority: 0,
      progress: payload.progress || 0,
      labelId: payload.label,
      expectedUsers,
      users: payload.users,
      costCodes: payload.costCodes?.map(parseGanttCostCodeKey),
      equipment: payload.equipment,
      materials: (payload.materials || []).map((material) => ({
        id: material.id,
        quantity: material.quantity,
      })),
      phaseId: payload.phaseId || null,
      actualStartDate: payload.actualStartDate
        ? payload.actualStartDate.valueOf() / 1000
        : null,
      actualEndDate: payload.actualEndDate
        ? payload.actualEndDate.valueOf() / 1000
        : null,
      parentDependencies: payload.parentDependencies,
      childDependencies: payload.childDependencies,
      notes: payload.notes,
    };

    let result;

    if (selectedTask.id) {
      result = await dispatch(
        updateGanttScheduleRow(selectedTask.ganttScheduleId, selectedTask.id, addPayload),
      );
    } else {
      result = await dispatch(addGanttScheduleRows(scheduleId, addPayload));
    }

    if (result) {
      hideDrawer();
    }
  };

  const onDelete = async (ganttScheduleId, rowId) => {
    if (await dispatch(deleteGanttScheduleRow(ganttScheduleId, rowId))) {
      hideDrawer();
    }
  };

  const onSort = ({ key }) => {
    setSortType(key);
  };

  window.addEventListener('resize', () => {
    setDocumentHeight(document.body.offsetHeight);
  });

  const readOnly = !Permissions.has('PROJECT_GANTT_SCHEDULE_WRITE');
  const offsetHeight = useMemo(() => {
    // Miscellaneous offset
    const baseOffset = 160;
    // Breadcrumb title + padding
    const breadcrumbHeight = 70;
    // Header + tabs + footer
    const projectOffsetHeight = 155;

    if (isFullScreen) {
      return baseOffset;
    }

    if (selectedProjectId) {
      return baseOffset + projectOffsetHeight;
    }

    return baseOffset + breadcrumbHeight;
  }, [isFullScreen, selectedProjectId]);

  const onExport = useCallback(() => {
    setActiveTab('schedule');
    openDrawer('export');
  }, [openDrawer]);

  const onEnsureScheduleView = useCallback(() => {
    setActiveTab('schedule');
  }, []);

  const exportGanttVisibleColumns = useMemo(() => buildGanttVisibleColumns({
    columnNames: exportVisibleColumnNames || [],
    visibleColumnMap,
    shiftConnectionFormatter,
    isMaster,
  }), [exportVisibleColumnNames, visibleColumnMap, shiftConnectionFormatter, isMaster]);

  let ganttVisibleColumns = ganttUserSettings.visibleColumns;

  // If exporting, use the selected export columns since we are capturing the canvas of the visible gantt schedule
  if (
    visibleDrawer === 'export'
    && exportViewType === GANTT_EXPORT_VIEW_TYPES.GANTT
    && exportVisibleColumnNames
  ) {
    ganttVisibleColumns = exportGanttVisibleColumns;
  }

  if (!Permissions.has('PROJECT_GANTT_SCHEDULE_READ')) {
    history.replace('/dashboard');
    return null;
  }

  return (
    <BreadCrumbContainer
      crumbs={crumbs}
      fullscreen={isFullScreen}
      showHeader={!selectedProjectId}
      bodyStyle={{ overflowX: 'hidden' }}
      redesignBodyStyle={{
        marginTop: 0,
      }}
      redesignCrumbStyle={{
        marginBottom: 5,
      }}
    >
      <input type="file" hidden ref={inputRef} onChange={onFileChange} />
      <Row id="gantt-schedule-container" style={{ flexDirection: 'row' }}>
        <GanttScheduleHeader
          viewType={viewType}
          onViewTypeChange={onViewTypeChange}
          onDateChange={onDateChange}
          date={viewDate}
          openDrawer={openDrawer}
          onImport={onImport}
          schedules={filteredSchedules}
          setProjectId={setProjectId}
          projectId={projectId}
          setScheduleId={setScheduleId}
          scheduleId={scheduleId}
          toggleFullscreen={toggleFullscreen}
          isFullScreen={isFullScreen}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          isSelected={!!selectedProjectId}
          onSort={onSort}
          sortType={sortType}
        />
      </Row>
      { scheduleId
        && (
          <>
            {
              activeTab === 'schedule'
                && (
                  <Row id="schedule-container">
                    {
                      filteredTasks.length > 0
                      && (
                      <Gantt
                        tasks={filteredTasks}
                        viewMode={viewType}
                        columnWidth={columnWidth * scale}
                        handleWidth={5}
                        viewDate={viewDate}
                        ganttHeight={documentHeight - offsetHeight}
                        onDoubleClick={onEventClick}
                        rowHeight={30}
                        weekendDays={nonWorkingDays}
                        onListItemClick={onTaskClick}
                        visibleColumns={ganttVisibleColumns}
                        weekHeaderDisplay={ganttUserSettings.weekHeaderDisplay || DEFAULT_WEEK_HEADER_DISPLAY}
                        readOnly={readOnly}
                        onDateChange={onEventDateChange}
                      />
                      )
                    }
                    {
                      filteredTasks.length === 0
                      && <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
                    }
                  </Row>
                )
            }
            {
              activeTab === 'list'
                && (
                  <GanttScheduleList
                    tasks={filteredTasks}
                    visibleColumns={ganttUserSettings.visibleColumns}
                    onEditClick={onEventClick}
                    isReadOnly={readOnly}
                  />
                )
            }
          </>
        )}
      <GanttScheduleAddDrawer
        visible={visibleDrawer === 'add'}
        onClose={hideDrawer}
        readOnly={readOnly}
        handleSubmit={onAddSubmit}
        row={selectedTask}
        onDelete={onDelete}
        userLabelMap={userLabelMap}
        positionMap={positionMap}
        showDispatchDrawer={showDispatchDrawer}
        showShiftSelectorModal={showShiftSelectorModal}
        scheduleId={scheduleId}
        projectId={projectId}
      />
      <GanttScheduleImportDrawer
        visible={visibleDrawer === 'import'}
        tasksToImport={newTasks}
        handleSubmit={onImportSubmit}
        onClose={hideDrawer}
        existingHolidays={ganttScheduleHolidays}
        newHolidays={newHolidays}
        setNewHolidays={setNewHolidays}
        newSettings={newSettings}
        existingSettings={ganttScheduleSettings}
      />
      <GanttScheduleSettingsDrawer
        visible={visibleDrawer === 'settings'}
        onClose={hideDrawer}
        holidays={ganttScheduleHolidays}
        settings={ganttScheduleSettings}
        userSettings={userSettings}
        handleSubmit={onSettingsSubmit}
        handleUserSettingsSubmit={onUserSettingsSubmit}
        scheduleId={scheduleId}
        isMaster={isMaster}
        history={history}
        schedules={formattedSchedules}
        setScheduleId={setScheduleId}
        selectedSchedule={selectedSchedule}
        onExport={onExport}
      />
      <FilterDrawer
        title={`Filters for ${selectedSchedule.name}`}
        visible={visibleDrawer === 'filters'}
        onClose={hideDrawer}
        filterViews={ganttScheduleFilterViews}
        CustomFilterTab={GanttScheduleCustomFilterTab}
        equipment={equipment}
        users={users}
        materials={materials}
        labels={ganttScheduleLabels}
        projects={filteredProjects}
        schedule={selectedSchedule}
        phases={phases}
        drawerProps={{
          getContainer: false,
        }}
      />
      <GanttScheduleExportDrawer
        visible={visibleDrawer === 'export'}
        onClose={hideDrawer}
        tasks={filteredTasks}
        userSettings={userSettings}
        isMaster={isMaster}
        onEnsureScheduleView={onEnsureScheduleView}
        visibleColumns={exportVisibleColumnNames}
        onVisibleColumnsChange={setExportVisibleColumnNames}
        exportViewType={exportViewType}
        onExportViewTypeChange={setExportViewType}
      />
      <GanttScheduleDispatchDrawer
        onClose={hideDispatchDrawer}
        visible={dispatchDrawerProps.visible}
        row={dispatchDrawerProps.row || selectedTask}
        isDisplay={dispatchDrawerProps.isDisplay}
        shiftId={dispatchDrawerProps.shiftId}
      />
      <FieldShiftModalSelector
        visible={showFieldShiftSelector}
        shiftIds={selectedTask?.shiftConnections}
        onSubmit={onShiftClick}
        onClose={() => setShowFieldShiftSelector(false)}
      />
    </BreadCrumbContainer>
  );
}

GanttSchedule.propTypes = {
  history: PropTypes.shape({
    replace: PropTypes.func.isRequired,
  }).isRequired,
  projectId: PropTypes.string,
};

GanttSchedule.defaultProps = {
  projectId: null,
};
